// Small formatting + date-window helpers shared by the analytics pages.

import type { CallMode, CallRecord, CallSource, InputMode, TimeseriesPoint } from "../../services/analytics";
import type { EngineName } from "../../services/voiceEngines";

export type InputModeFilter = InputMode | "all";
export type SourceFilter = CallSource | "all";
export type EngineFilter = EngineName | "all";
export type RangePreset = "7d" | "30d" | "90d";

export const RANGE_PRESETS: { key: RangePreset; label: string; days: number }[] = [
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
];

// What a call's chip says. `inputMode` (what the user did) wins over `mode`
// (the pipeline): typed chats ride the voice pipeline, so `mode` alone
// labelled them "voice" even though they have no audio. `input_mode` and
// `user_msg_count` are only written when a call closes normally, so open and
// orphaned rows must be checked first: both read as "no user turn" otherwise.
export interface InputLabel {
  tone: "voice" | "typed" | "mixed" | "none" | "live" | CallMode;
  label: string; // chip text
  detail: string; // drawer "Input" row text
  title: string; // tooltip
}

type InputFields = Pick<CallRecord, "mode" | "inputMode" | "userMsgCount" | "endedAt" | "endReason">;

export function inputLabel(call: InputFields): InputLabel {
  if (!call.endedAt) {
    return {
      tone: "live",
      label: "live",
      detail: "call in progress",
      title: "Call in progress: input mode is recorded when it ends",
    };
  }
  switch (call.inputMode) {
    case "voice":
      return { tone: "voice", label: "voice", detail: "voice", title: "Every user turn was spoken" };
    case "text":
      return { tone: "typed", label: "typed", detail: "typed", title: "Every user turn was typed" };
    case "mixed":
      return { tone: "mixed", label: "mixed", detail: "mixed", title: "Spoken and typed turns" };
  }
  if (call.endReason === "orphaned") {
    return {
      tone: "none",
      label: "unknown",
      detail: "unknown (server stopped mid-call)",
      title: "The server stopped before the call closed, so its input was never recorded",
    };
  }
  if (call.userMsgCount === 0) {
    return { tone: "none", label: "no turns", detail: "no user turn", title: "No user turn was recorded" };
  }
  // Rows from before input_mode was recorded: fall back to the pipeline.
  return {
    tone: call.mode,
    label: call.mode === "voice" ? "voice" : "chat",
    detail: "not recorded",
    title: "Older row: input mode was not recorded",
  };
}

// The pipeline closes the row first and attaches the recording after a
// bounded upload (close ≤25s + upload ≤60s), so a recently ended call with
// no recording yet is not a failure.
export const RECORDING_ATTACH_GRACE_MS = 120_000;

export function endedWithin(endedAt: string | null, ms: number, now = Date.now()): boolean {
  return endedAt !== null && now - new Date(endedAt).getTime() < ms;
}

export interface DateWindow {
  from: string; // ISO datetime
  to: string; // ISO datetime
}

export function windowFromPreset(preset: RangePreset): DateWindow {
  const days = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 30;
  const to = new Date();
  const from = new Date(to.getTime() - days * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

// The equal-length window immediately before `win` (for delta comparisons).
export function previousWindow(win: DateWindow): DateWindow {
  const from = new Date(win.from).getTime();
  const to = new Date(win.to).getTime();
  const span = Math.max(to - from, 0);
  return { from: new Date(from - span).toISOString(), to: new Date(from).toISOString() };
}

// `input_mode` param for the API: "all" means omit.
export function apiInputMode(mode: InputModeFilter): InputMode | undefined {
  return mode === "all" ? undefined : mode;
}

// `source` param for the API: "app" is the server default, so omit it; the
// other values are sent explicitly.
export function apiSource(source: SourceFilter): "studio_bench" | "all" | undefined {
  return source === "app" ? undefined : source;
}

// Parse a ?source= URL param back into a filter value (invalid/missing = app).
export function parseSourceParam(value: string | null): SourceFilter {
  return value === "studio_bench" || value === "all" ? value : "app";
}

const ENGINE_FILTERS: EngineFilter[] = ["all", "cascade", "gemini_live", "elevenlabs"];

// Parse a ?engine= URL param back into a filter value (invalid/missing = all).
export function parseEngineParam(value: string | null): EngineFilter {
  return ENGINE_FILTERS.find((e) => e === value) ?? "all";
}

// `engine` param for the API: "all" means omit.
// A YYYY-MM-DD URL param (Calls' ?from= and ?to=, set by Voice engines →
// Results links), else null.
export function parseDateParam(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)) ? value : null;
}

export function apiEngine(engine: EngineFilter): EngineName | undefined {
  return engine === "all" ? undefined : engine;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatSentiment(score: number | null | undefined): string {
  if (score == null || !Number.isFinite(score)) return "—";
  const rounded = score.toFixed(2);
  return score > 0 ? `+${rounded}` : rounded; // toFixed keeps the minus sign
}

export type SentimentTone = "pos" | "neu" | "neg";

export function sentimentTone(score: number | null | undefined): SentimentTone {
  if (score == null || !Number.isFinite(score)) return "neu";
  if (score >= 0.15) return "pos";
  if (score <= -0.15) return "neg";
  return "neu";
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function truncateUid(uid: string, len = 8): string {
  if (!uid) return "—";
  return uid.length > len ? `${uid.slice(0, len)}…` : uid;
}

export function excerpt(text: string | null | undefined, max = 90): string {
  if (!text) return "—";
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

// Expand the API's (possibly sparse) day buckets into one point per day of the
// requested window so chart bars line up with the calendar.
export function fillDays(points: TimeseriesPoint[], fromIso: string, toIso: string): TimeseriesPoint[] {
  const byDate = new Map(points.map((p) => [p.date, p]));
  const out: TimeseriesPoint[] = [];
  const cursor = new Date(fromIso);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(toIso);
  // Guard against a bad range producing an endless loop.
  for (let i = 0; cursor <= end && i < 400; i++) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(
      cursor.getDate(),
    ).padStart(2, "0")}`;
    out.push(byDate.get(key) ?? { date: key, calls: 0, avgDurationMs: null, avgSentiment: null });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

// Percentage delta vs a previous value; null when not computable.
export function pctDelta(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}
